import React from 'react';
import { Box, Chip, Grid, Switch, Tooltip, Typography } from '@mui/material';
import { CheckCircle, Cancel } from '@mui/icons-material';

import { formatMonitoringDate } from '../../utils/formatMonitoringDate';

type Props = {
  title: string;
  subtitle?: string;
  isActive: boolean;
  badge?: string;
  /** Toggle bidirecional: checked=true ativo, false inativo. */
  onToggle?: (checked: boolean) => void;
  /** @deprecated Use onToggle. Mantido para compatibilidade. */
  onDeactivate?: () => void;
  toggleLoading?: boolean;
  /** @deprecated Use toggleLoading */
  deactivateLoading?: boolean;
};

/** Card compacto PA para item monitorado (preferências / histórico). */
export function MonitoringActiveItemCard({
  title,
  subtitle,
  isActive,
  badge,
  onToggle,
  onDeactivate,
  toggleLoading,
  deactivateLoading,
}: Props) {
  const loading = toggleLoading ?? deactivateLoading ?? false;
  const handleToggle = onToggle ?? (onDeactivate ? () => onDeactivate() : undefined);

  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: '10px',
        border: '1px solid #DEDEDE',
        backgroundColor: '#fff',
        boxShadow: '0px 2px 8px rgba(0,0,0,0.06)',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="body2" fontWeight={700} noWrap title={title}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        {handleToggle && (
          <Tooltip title={isActive ? 'Desativar monitoramento' : 'Reativar monitoramento'}>
            <Switch
              size="small"
              checked={isActive}
              color="success"
              disabled={loading}
              onChange={(_, checked) => {
                if (onToggle) {
                  onToggle(checked);
                } else if (!checked) {
                  onDeactivate?.();
                }
              }}
            />
          </Tooltip>
        )}
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 'auto' }}>
        {badge && <Chip label={badge} size="small" color="primary" variant="outlined" />}
        <Chip
          label={isActive ? 'Ativo' : 'Inativo'}
          size="small"
          color={isActive ? 'success' : 'default'}
          icon={isActive ? <CheckCircle /> : <Cancel />}
        />
      </Box>
    </Box>
  );
}

export function MonitoringActiveItemGrid({ children }: { children: React.ReactNode }) {
  return (
    <Grid container spacing={1.5}>
      {React.Children.map(children, (child) => (
        <Grid item xs={12} sm={6} md={4} key={(child as React.ReactElement).key}>
          {child}
        </Grid>
      ))}
    </Grid>
  );
}

export function formatMonitoringActiveSince(timestamp?: string | null): string {
  if (!timestamp) return 'Ativo';
  return `Ativo desde ${formatMonitoringDate(timestamp)}`;
}
